import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { defaultPaletteColor } from '../constants-types/ColorPalettePicker.model';
import {
    SurveyUserTypes,
    Impression,
    PatternType,
    InspireAccessStatus
} from '../constants-types/Enums';
import { BehavioralAssessment } from './BehavioralAssessment.model';
import { CognitiveAssessment } from './CognitiveAssessment.model';
import { DisplayableInTree } from './DisplayableInTree';
import { Folder, SurveyUserFolder } from './Folder.model';
import { JobStub } from './Job/JobStub.model';
import { Job } from './Job.model';
import { ModuleName } from './ModuleAccess';
import { Pattern } from './Pattern.model';
import { PatternInsights } from './PatternInsights.interface';
import {
    ReferencePatternType,
    ReferencePatternsInformation
} from './ReferencePatternType.model';
import { ResultBasics } from './ResultBasics.model';

export class PersonStub {
    id: string;
    readonly firstName: string;
    readonly lastName: string;

    constructor(obj?: Partial<PersonStub>) {
        Object.assign(this, obj);
    }

    fullName(lastNameFirst = false): string {
        return Person.fullName(this, lastNameFirst);
    }
}

export class OrgPerson extends PersonStub implements DisplayableInTree {
    readonly parentId: string;
    readonly children: OrgPerson[];
    readonly referencePatternId: number;
    readonly hasPendingBA: boolean;
    readonly department: string;
    readonly isExcludedFromSurvey: boolean;

    constructor(obj?: Partial<OrgPerson>) {
        super();
        Object.assign(this, obj);
    }
}

export class OrgPersonInfo extends OrgPerson {
    readonly email: string;
    readonly personId: string; // the id of OrgPerson is the orgPersonId

    constructor(obj?: Partial<OrgPersonInfo>) {
        super();
        Object.assign(this, obj);
    }
}

export class PartialPerson extends PersonStub {
    readonly middleName?: string;
    readonly email: string;
    readonly job: JobStub | Job;

    readonly behavioralAssessments: BehavioralAssessment[] = [];
    type: SurveyUserTypes = SurveyUserTypes.Candidate; // should be an enum

    constructor(obj?: Partial<PartialPerson>) {
        super();
        Object.assign(this, obj);
    }

    get personDetails_route(): string[] {
        switch (this.type) {
            case SurveyUserTypes.Candidate:
                if (this.job)
                    return [
                        `person`,
                        `candidate`,
                        `${this.job.id}`,
                        `${this.id}`
                    ];
                return [`person`, `candidate`, `none`, `${this.id}`];
            case SurveyUserTypes.Employee:
            case SurveyUserTypes.FormerEmployee:
            default:
                return ['person', `${this.id}`];
        }
    }
}

export class Person extends PartialPerson implements ResultBasics {
    readonly gender: string;
    readonly userId: string;
    readonly folder: Folder;

    readonly cognitiveAssessments: CognitiveAssessment[] = [];

    // lastCompleted is a little unfortunate since it is technically duplicated
    // data, but we get an abbreviated set of data for table display and want
    // this data shown despite not having full info on assessments
    readonly lastCompleted: Date;
    readonly lastSent: Date;
    readonly lastUpdated: Date;

    readonly jobFit: JobFit;

    _referencePattern: ReferencePatternType;

    job: Job;
    // Default job access to true unless we've checked
    hasJobAccess = true;
    displayColor: number = defaultPaletteColor; // default person background color: bg-color-person
    isManager: boolean;

    static sortByName(a: Person, b: Person) {
        let retval = 0;
        if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) {
            retval = -1;
        } else if (b.lastName.toLowerCase() > a.lastName.toLowerCase()) {
            retval = 1;
        } else if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) {
            retval = -1;
        } else if (b.firstName.toLowerCase() > a.firstName.toLowerCase()) {
            retval = 1;
        }
        return retval;
    }

    static fullName(
        person: {
            firstName: string;
            lastName: string;
        },
        lastNameFirst = false
    ): string {
        return lastNameFirst
            ? `${person.lastName}, ${person.firstName}`
            : `${person.firstName} ${person.lastName}`;
    }

    static displayInitials(person: { firstName: string; lastName: string }) {
        return `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`;
    }

    constructor(obj?: Partial<Person>) {
        super();
        Object.assign(this, obj);
    }

    updateJobFit(jobFitUpdate: JobFit): Person {
        // To specifically overwrite the JobFit(readonly type)
        const baseObject: any = {};
        return new Person(
            Object.assign(baseObject, this, { jobFit: jobFitUpdate })
        );
    }

    updateNameAndEmail(personLiteUpdate: PersonLiteUpdate) {
        const baseObject: any = {};
        // To specifially overwrite the FirstName, LastName and Email
        return new Person(
            Object.assign(baseObject, this, {
                firstName: personLiteUpdate.firstName,
                lastName: personLiteUpdate.lastName,
                email: personLiteUpdate.email
            })
        );
    }

    updateLastSentDate(lastSent: Date): Person {
        // used to update the lastSent field using immutable update pattern
        const baseObject: any = {};
        // ensure a value was provided otherwise return unchanged object
        if (!lastSent) {
            return this;
        }
        return new Person(
            Object.assign(baseObject, this, { lastSent: lastSent })
        );
    }

    getDisplay() {
        return this.fullName(true);
    }

    hasId(): boolean {
        // explicit data conversion
        return !!this.id;
    }

    hasAssessments(): boolean {
        return this.behavioralAssessments.length > 0;
    }

    hasPending(): boolean {
        return this.hasPendingBehavioral() || this.hasPendingCognitive();
    }

    hasPendingBehavioral(): boolean {
        return (
            this.latestBehavioralAssessment &&
            !this.latestBehavioralAssessment.completedDate
        );
    }

    hasPendingCognitive(): boolean {
        return (
            this.highestCognitiveAssessment &&
            !this.highestCognitiveAssessment.completedDate
        );
    }

    hasBehavioralPattern(): boolean {
        return this.getBehavioralAssessmentsWithPatterns().length > 0;
    }

    hasCompletedCognitiveAssessment(): boolean {
        return this.getCompletedCognitiveAssessments().length > 0;
    }

    reportLink(fullPath = false): string {
        const relativePath = `/person/${this.id}/behavioralReport`;
        if (!fullPath) {
            return relativePath;
        }
        return window.location.origin + relativePath;
    }

    static getPersonPageUrl(person: Person): string {
        return `/person/${person.id}`;
    }

    getPersonPageUrl(): string {
        return Person.getPersonPageUrl(this);
    }

    get self(): Pattern {
        const original = this.originalAssessment;
        return original ? original.pattern.self : null;
    }

    // Hey Superfans, this misbehaves when the Person is created
    // from the index because the 'original' assessment has an empty
    // sentData. I didn't tackle this because I was afraid of regressions...
    // You could fix this to correctly to get the latest when you get
    // incomplete info from a search index call, but you would need
    // to be sure that a change here would not cause a regression by
    // checking and testing every use of this.
    get latestBehavioralAssessment(): BehavioralAssessment {
        if (!this.behavioralAssessments.length) {
            return null;
        }
        return this.behavioralAssessments.reduce(
            (
                lastValue: BehavioralAssessment,
                currentValue: BehavioralAssessment
            ) => {
                if (!lastValue.sentDate) {
                    return currentValue;
                }
                return currentValue.sentDate &&
                    lastValue.sentDate.getTime() >
                        currentValue.sentDate.getTime()
                    ? lastValue
                    : currentValue;
            }
        );
    }

    // Why is reference pattern here? Originally I had put it on the assessments because
    // that made sense to me, but the core team and hence the APIs seemed focused on only
    // having that information attached to the user. - Moving this comment here 03/05/2020
    get referencePattern(): ReferencePatternType {
        // Calculate the reference based on the info we have
        if (!this._referencePattern && this.hasBehavioralPattern()) {
            this._referencePattern = ReferencePatternsInformation.find(
                (ref) => ref.id === this.originalAssessment.patternNumber // Reference pattern should be oldest assessment
            );
            return this._referencePattern;
        } else if (this._referencePattern) {
            return this._referencePattern;
        }
        return null;
    }

    set referencePattern(val: ReferencePatternType) {
        this._referencePattern = val;
    }

    private getBehavioralAssessmentsWithPatterns(): BehavioralAssessment[] {
        if (!this.behavioralAssessments) {
            return [];
        }
        return this.behavioralAssessments.filter(
            (b) => !b.isPendingOrExpired() && b.hasPattern()
        );
    }

    /**
     * Newest first.
     */
    private sortAssessmentsByCompletedTime(
        assessments: BehavioralAssessment[],
        oldestFirst = false
    ): BehavioralAssessment[] {
        assessments.sort((a, b) => {
            if (!a.completedDate) {
                return 1;
            } else if (!b.completedDate) {
                return -1;
            } else {
                return b.completedDate.getTime() - a.completedDate.getTime();
            }
        });
        return oldestFirst ? assessments.reverse() : assessments;
    }
    private getEarliestAssessmentFromList(
        assessments: BehavioralAssessment[]
    ): BehavioralAssessment {
        return this.sortAssessmentsByCompletedTime(assessments, true)[0];
    }
    private getLatestAssessmentFromList(
        assessments: BehavioralAssessment[]
    ): BehavioralAssessment {
        return this.sortAssessmentsByCompletedTime(assessments)[0];
    }

    get originalAssessment(): BehavioralAssessment {
        const assessmentsWithPatterns =
            this.getBehavioralAssessmentsWithPatterns();
        if (assessmentsWithPatterns.length < 1) {
            return null;
        }
        return this.getEarliestAssessmentFromList(assessmentsWithPatterns);
    }

    /**
     * For someone who has an "original" completed assessment, returns the
     * person who administered that assessment.
     * For people with pending assessments it will return the person who
     * sent the most recent one.
     */
    get behavioralAssessmentAdministeredBy(): string {
        if (this.originalAssessment) {
            return this.originalAssessment.administeredBy;
        }
        const mostRecentAssessment = this.getLatestAssessmentFromList(
            this.behavioralAssessments
        );
        if (mostRecentAssessment) {
            return mostRecentAssessment.administeredBy;
        }
        return '';
    }

    get originalAssessmentId(): string {
        const original = this.originalAssessment;
        return original ? original.id : null;
    }

    private getCompletedCognitiveAssessments(): CognitiveAssessment[] {
        if (!this.cognitiveAssessments) {
            return [];
        }
        return this.cognitiveAssessments.filter((b) => b.hasScore());
    }

    // Among completed Cog assessments, the one with the highest score
    // Warning: if there are no completed assessments, this will return NULL
    get highestCognitiveAssessment(): CognitiveAssessment {
        const completedAssessments = this.getCompletedCognitiveAssessments();
        if (!completedAssessments.length) {
            return null;
        }
        return this.cognitiveAssessments.reduce(
            (
                lastValue: CognitiveAssessment,
                currentValue: CognitiveAssessment
            ) => {
                if (lastValue.score > currentValue.score) {
                    return lastValue;
                } else if (lastValue.score === currentValue.score) {
                    // Tiebreaker by assessment completed date
                    if (
                        lastValue.completedDate.getTime() >
                        currentValue.completedDate.getTime()
                    ) {
                        return lastValue;
                    } else {
                        return currentValue;
                    }
                } else {
                    return currentValue;
                }
            }
        );
    }

    get highestCognitiveAssessmentAdministeredBy(): string {
        const cog = this.highestCognitiveAssessment;
        if (!cog) {
            return null;
        }
        return cog.administeredBy;
    }

    private getPendingCognitiveAssessments(): CognitiveAssessment[] {
        if (!this.cognitiveAssessments) {
            return [];
        }
        return this.cognitiveAssessments.filter((b) => !b.completedDate);
    }

    get latestPendingCognitiveAssessment(): CognitiveAssessment {
        const completedAssessments = this.getPendingCognitiveAssessments();
        if (!completedAssessments.length) {
            return null;
        }
        return this.cognitiveAssessments.reduce(
            (
                lastValue: CognitiveAssessment,
                currentValue: CognitiveAssessment
            ) => {
                if (
                    lastValue.sentDate.getTime() >
                    currentValue.sentDate.getTime()
                ) {
                    return lastValue;
                } else {
                    return currentValue;
                }
            }
        );
    }

    get mostRecentPendingAssessmentAdministeredBy(): string {
        const cog = this.latestPendingCognitiveAssessment;
        if (!cog) {
            return null;
        }
        return cog.administeredBy;
    }

    // When generating the thumbnail display, we may want to show either the highest scored cognitive
    // or the most recently sent pending cog assessment.  This method is intended to determine what is
    // the priority in what is displayed first.
    getCognitiveAssessmentForThumbnailDisplay(
        returnPendingCog = false
    ): CognitiveAssessment {
        if (returnPendingCog) {
            // When getting pending cogs, we want to first look to see if there are any pending cogs.
            // if there are not any pending cogs, we check and see if there are completed cogs because
            // when we get pending assessments you could have an incomplete BA but completed CA.
            return this.latestPendingCognitiveAssessment
                ? this.latestPendingCognitiveAssessment
                : this.highestCognitiveAssessment;
        } else {
            // Like above but instead we first look for highest completed cog, if we don't have any we then
            // check for pending cogs because we may also have complete/incomplete BAs to account for as well.
            return this.highestCognitiveAssessment
                ? this.highestCognitiveAssessment
                : this.latestPendingCognitiveAssessment;
        }
    }

    getCognitiveAdministeredByForDisplay(returnPendingCog = false): string {
        if (returnPendingCog) {
            return this.mostRecentPendingAssessmentAdministeredBy
                ? this.mostRecentPendingAssessmentAdministeredBy
                : this.highestCognitiveAssessmentAdministeredBy;
        } else {
            return this.highestCognitiveAssessmentAdministeredBy
                ? this.highestCognitiveAssessmentAdministeredBy
                : this.mostRecentPendingAssessmentAdministeredBy;
        }
    }

    getCopyForReports() {
        return new Person(Object.assign({}, this, { folder: null }));
    }

    clientSideUpdate(personUpdate: PersonUpdate): Person {
        const clearJob =
            personUpdate.job === 'RemoveJob' ? { job: undefined } : {};
        return new Person(Object.assign({}, this, personUpdate, clearJob));
    }
}

export class PersonWithDetails extends Person {
    patternInsights: PatternInsights;
    readonly defaultCognitiveTime: number = null;

    constructor(
        coreValues?: Partial<PersonWithDetails> | Person,
        otherSundryvalues?: Partial<PersonWithDetails>
    ) {
        super();
        Object.assign(this, coreValues);
        Object.assign(this, otherSundryvalues);
    }

    clientSideUpdate(
        personUpdate: PersonUpdate,
        jobFit?: JobFit
    ): PersonWithDetails {
        const baseObject: any = {};
        if (jobFit) {
            baseObject.jobFit = jobFit;
        }
        const clearJob =
            personUpdate.job === 'RemoveJob' ? { job: undefined } : {};
        return new PersonWithDetails(
            Object.assign(baseObject, this, personUpdate, clearJob)
        );
    }
}

export class PizzaPlaygroundSessionPersonWithVisibility {
    personObject?: Person;
    visible = true;
    personId: string;
    displayColor: number;
    firstName: string;
    lastName: string;
    selfPattern: Pattern;
    referencePatternType: ReferencePatternType;

    constructor(args: Partial<PizzaPlaygroundSessionPersonWithVisibility>) {
        Object.assign(this, args);
    }
}

export class PersonUpdate {
    originalObject?: Person;

    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    folder: Folder;
    type: SurveyUserTypes;
    job: Job | 'RemoveJob';

    constructor(obj?: Partial<PersonUpdate>) {
        Object.assign(this, obj);
    }

    fullName() {
        return Person.fullName(this);
    }
}
export class JobFit {
    readonly personImpression: Impression;
    readonly overallFitScore: number;
    readonly behavioralFitScore: number;
    readonly cognitiveFitScore: number;

    constructor(obj?: any) {
        Object.assign(this, obj);
    }
}

export class PersonLiteUpdate {
    firstName: string;
    lastName: string;
    email: string;
    impression: Impression;
    id: string;

    constructor(obj?: Partial<PersonLiteUpdate>) {
        Object.assign(this, obj);
    }
}

/**
 * Model used to populate Employees within Modules
 */
export class OrgMember {
    surveyUserGuid: string;
    firstName: string;
    lastName: string;
    email: string;
    managerGuid?: string;
    managerName?: string;
    managerEmail?: string;
    jobTitle?: string;
    referencePatternInfo: {
        patternType: PatternType;
        latestPendingAssessmentKey: string;
    };
    lastModifiedTime: Date;
    positionGuid?: string;
    inspireAccessStatus: InspireAccessStatus;
    userGuid?: string;
    createdTime: Date;
    isInvitationPending: boolean;
    modulesAccessToGrant: Partial<{
        [key in ModuleName]: PermissionsModuleAccessLevel;
    }>;
    folder?: SurveyUserFolder;
    dateOfHire?: Date;
    department?: string;
    isDeleted: boolean;
    isManager?: boolean;
    scoreId?: string;
    pendingAssessmentDate?: Date;
    location?: string;

    constructor(obj?: Partial<OrgMember>) {
        Object.assign(this, obj);
    }
}
